/**
 * Client-side prediction of every economy change. The server recomputes each one from the
 * queued op and its snapshot replaces these numbers on the next sync (no local ledger).
 * Every function mutates the given PlayerState; call them inside store.update().
 */
import {
  claimDailyGift,
  dailyGiftStatus,
  levelForXp,
  offlineSales,
  shopRate,
  streakOnPlay,
  xpForRound,
  type RoundOutcome,
  type StreakUpdate,
} from '@mp/core';
import { BOOSTERS, cityShops, economy, rareItemDef, shopDef, type BoosterKey, type Grant } from '../config/content.js';
import { totalStars, unlocksBetween, upgradeCount, upgradeState } from './progress.js';
import type { PlayerState, Style } from './state.js';

/** Apply a grant (or spend, with negative amounts) to balances. */
export function applyGrant(s: PlayerState, g: Grant & { rareItem?: string }, mult = 1): void {
  s.coins += (g.coins ?? 0) * mult;
  s.gems += (g.gems ?? 0) * mult;
  for (const b of BOOSTERS) s.inventory[b] += (g.boosters?.[b] ?? 0) * mult;
  if (g.rareItem) s.rareItems[g.rareItem] = (s.rareItems[g.rareItem] ?? 0) + mult;
}

export interface RoundSummary {
  won: boolean;
  stars: number;
  coins: number;
  xp: number;
  starsDelta: number;
  newBest: boolean;
  prevBest: number;
  levelFrom: number;
  levelTo: number;
  unlocks: string[];
  streak: StreakUpdate;
  streakGrant: Grant | null;
}

export function applyRound(s: PlayerState, levelId: string, o: RoundOutcome, today: number): RoundSummary {
  const won = o.result === 'win';
  const prev = s.levels[levelId] ?? { bestStars: 0, bestTaps: 0, plays: 0 };
  const prevBest = prev.bestStars;
  const next = { ...prev, plays: prev.plays + 1 };
  if (won) {
    next.bestStars = Math.max(prev.bestStars, o.stars);
    next.bestTaps = prev.bestTaps > 0 ? Math.min(prev.bestTaps, o.taps) : o.taps;
  }
  s.levels[levelId] = next;
  const starsBefore = s.stars;
  s.stars = totalStars(s);
  s.coins += o.coins;
  const xp = xpForRound(won, o.stars, economy.xp);
  const levelFrom = s.level;
  s.xp += xp;
  s.level = levelForXp(economy.xp.table, s.xp);
  if (won) delete s.failStreak[levelId];
  else s.failStreak[levelId] = (s.failStreak[levelId] ?? 0) + 1;
  const streak = streakOnPlay(s.streak, today);
  s.streak = streak.state;
  let streakGrant: Grant | null = null;
  if (streak.milestone !== null) {
    streakGrant = economy.streakMilestones[String(streak.milestone)] ?? null;
    if (streakGrant) applyGrant(s, streakGrant);
  }
  return {
    won,
    stars: o.stars,
    coins: o.coins,
    xp,
    starsDelta: s.stars - starsBefore,
    newBest: won && o.stars > prevBest && prevBest > 0,
    prevBest,
    levelFrom,
    levelTo: s.level,
    unlocks: unlocksBetween(levelFrom, s.level),
    streak,
    streakGrant,
  };
}

/** Double tips (rewarded ad): the tips part of the win is granted again. */
export function applyDoubleTips(s: PlayerState, tips: number): void {
  if (tips > 0) s.coins += tips;
}

export type UpgradeResult = { ok: true; coins: number; rareItem: string | null } | { ok: false; reason: 'locked' | 'owned' | 'short' | 'rare' };

export function applyUpgrade(s: PlayerState, shopId: string, slot: string, style: Style): UpgradeResult {
  const st = upgradeState(s, shopId, slot);
  if (st.status !== 'ready') return { ok: false, reason: st.status === 'owned' ? 'owned' : st.status === 'short' ? 'short' : st.status === 'rare' ? 'rare' : 'locked' };
  s.coins -= st.cost.coins;
  if (st.rareItem) s.rareItems[st.rareItem] = Math.max(0, (s.rareItems[st.rareItem] ?? 0) - 1);
  const shop = (s.shops[shopId] ??= { upgrades: {} });
  shop.upgrades[slot] = style;
  return { ok: true, coins: st.cost.coins, rareItem: st.rareItem };
}

export function isGrandOpeningDue(s: PlayerState, shopId: string): boolean {
  const shop = shopDef(shopId);
  return !!shop && upgradeCount(s, shopId) >= shop.upgrades.length && !s.flags.grandOpenings.includes(shopId);
}

// ---------- rare items (SO-01 gem fallback) ----------
export function rareItemPrice(id: string): number {
  return rareItemDef(id)?.gemPrice ?? 120;
}

export function buyRareItem(s: PlayerState, id: string): boolean {
  const price = rareItemPrice(id);
  if (s.gems < price) return false;
  applyGrant(s, { gems: -price, rareItem: id });
  return true;
}

// ---------- boosters (GP-06) ----------
export function boosterPrice(key: BoosterKey): number {
  return economy.boosterPrices[key];
}

/** Predict a gem purchase of one booster (queue `op.buyBooster` alongside). */
export function buyBooster(s: PlayerState, key: BoosterKey): boolean {
  const price = boosterPrice(key);
  if (s.gems < price) return false;
  applyGrant(s, { gems: -price, boosters: { [key]: 1 } });
  return true;
}

/** Inventory debit; the server debits again from the round's boostersUsed. */
export function consumeBooster(s: PlayerState, key: BoosterKey): boolean {
  if (s.inventory[key] <= 0) return false;
  s.inventory[key]--;
  return true;
}

// ---------- rescue (GP-04, Flow C) ----------
export type RescueMethod = 'free' | 'ad' | 'gems' | 'booster';

export interface RescueOffer {
  free: boolean;
  gems: number;
  canAffordGems: boolean;
  boosters: number;
}

export function rescueOffer(s: PlayerState, kind: 'slot' | 'time'): RescueOffer {
  return {
    free: economy.rescue.firstFree && !s.flags.freeRescueUsed,
    gems: economy.rescue.gems,
    canAffordGems: s.gems >= economy.rescue.gems,
    boosters: kind === 'slot' ? s.inventory.extraSlot : 0,
  };
}

/** Predict what a rescue costs; the round op carries the method (and an ad nonce) to the server. */
export function payRescue(s: PlayerState, method: RescueMethod): boolean {
  switch (method) {
    case 'free':
      if (s.flags.freeRescueUsed) return false;
      s.flags.freeRescueUsed = true;
      return true;
    case 'gems':
      if (s.gems < economy.rescue.gems) return false;
      s.gems -= economy.rescue.gems;
      return true;
    case 'booster':
      return consumeBooster(s, 'extraSlot');
    case 'ad':
      return true;
  }
}

// ---------- daily gift (MT-06) ----------
export function dailyGift(s: PlayerState, today: number): { claimable: boolean; day: number; grant: Grant } {
  const st = dailyGiftStatus(s.daily, today);
  return { claimable: st.claimable, day: st.nextCycleDay + 1, grant: economy.dailyGift[st.nextCycleDay] ?? {} };
}

/** Predict the claim (queue `op.dailyGift(localDate)` alongside). */
export function claimDaily(s: PlayerState, today: number): Grant | null {
  const res = claimDailyGift(s.daily, today);
  if (!res.ok) return null;
  s.daily = res.state;
  const grant = economy.dailyGift[res.giftDay - 1] ?? {};
  applyGrant(s, grant);
  return grant;
}

// ---------- offline sales (MT-03) ----------
/** Same rule as the server: shops count in order while the previous one is complete. */
export function offlineRate(s: PlayerState): number {
  let rate = 0;
  const list = cityShops(s.city);
  for (let i = 0; i < list.length; i++) {
    const shop = list[i]!;
    const prev = list[i - 1];
    if (prev && upgradeCount(s, prev.id) < prev.upgrades.length) break;
    rate += shopRate(shop.baseRatePerHour, upgradeCount(s, shop.id), economy.offlineSales.pctPerUpgrade);
  }
  return rate;
}

export function offlineAmount(s: PlayerState, now: number): number {
  return offlineSales(offlineRate(s), now - s.lastCollectAt, economy.offlineSales.capHours);
}

/** Predict a collect (queue `op.offlineCollect(adNonce)` alongside). Below the popup minimum time keeps accruing. */
export function collectOffline(s: PlayerState, amount: number, now: number, doubled: boolean): number {
  if (amount < economy.offlineSales.minPopupCoins) return 0;
  s.lastCollectAt = now;
  const coins = doubled ? amount * 2 : amount;
  s.coins += coins;
  return coins;
}
